import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown, X, FilePlus, Eye, Edit, Trash } from 'lucide-react';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import Spinner from '../components/ui/spinner';
import CustomerApi from '../customer/customerApi';
import { Customer } from '../customer/customerType';
import { formatTimestamp, formatAddress } from '../lib/global_function';

const CustomerPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Customer', path: '/main/customer' },
  ];
  
  const navigate = useNavigate();
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  
  const selectedCustomer = customers.find(c => c.customer_id === selectedCustomerId) || null;
  
  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        setLoading(true);
        const fetchedCustomers = await CustomerApi.get();
        setCustomers(fetchedCustomers);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, []);
  
  const handleSortByLastName = (order: 'asc' | 'desc') => {
    const sortedCustomers = [...customers].sort((a, b) => {
      if (order === 'asc') {
        return a.last_name.localeCompare(b.last_name);
      } else {
        return b.last_name.localeCompare(a.last_name);
      }
    });
    setCustomers(sortedCustomers);
  };
  
  const handleSortByDate = (order: 'asc' | 'desc') => {
    const sortedCustomers = [...customers].sort((a, b) => {
      const aDate = a.created_at ?? '';
      const bDate = b.created_at ?? '';

      return order === 'asc'
        ? aDate.localeCompare(bDate)
        : bDate.localeCompare(aDate);
    });
    setCustomers(sortedCustomers);
  };
  
  const handleClearSelection = () => {
    setSelectedCustomerId(null);
  };

  const handleEditCustomer = (customer: Customer) => {
    navigate('/main/customer/update', { state: { customer } });
  };

  const handleDeleteCustomer = () => {
    if (selectedCustomer) {
      navigate('/main/customer/delete', { state: { customer: selectedCustomer } });
    }
  };

  const handleCreateProject = () => {
    if (selectedCustomer) {
      navigate('/main/project/create', { state: { customer: selectedCustomer } });
    }
  };

  const handleViewProject = () => {
    if (selectedCustomer) {
      navigate('/main/project', { state: { customer: selectedCustomer } });
    }
  };

  const handleTrackEstimateAndInvoice = () => {
    if (selectedCustomer) {
      navigate('/main/customer/documents_sent', { state: { customer: selectedCustomer } });
    }
  };

  const handleCustomerPayment = () => {
    if (selectedCustomer) {
      navigate('/main/customerpayment', { state: { customer: selectedCustomer } });
    }
  };

  const handleCreateCustomer = () => {
    navigate('/main/customer/create');
  };

  const handleEdit = () => {
    navigate('/main/customer/update', { state: { customer: selectedCustomer } });
  };
  
  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />
        
        <div className="mt-6">
          <div className="mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
                Customer List
              </h1>
              <Button size="medium" variant="default" onClick={handleCreateCustomer}>
                <FilePlus size={18} />
                Create Customer
              </Button>
            </div>

            <p className="text-sm text-paragraph">
              Select radio button in front of name to view billing, project and document sent, create a project or edit/delete customer.
            </p>
          </div>
          
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}
          
          {/* Loading State */}
          {loading ? (
            <Spinner />
          ) : (
            <>
              <div className="mb-6 p-4 bg-gray-50 rounded-lg flex flex-wrap items-center gap-3 text-sm">
                <div className="flex items-center gap-2">
                  {selectedCustomer ? (
                    <>
                      <button onClick={handleClearSelection} className="text-gray-600 hover:text-gray-900" title="Clear Selection">
                        <X className="size-5" />
                      </button>
                      <Badge variant="default">1 customer selected</Badge>
                    </>
                  ) : (
                    <span className="text-gray-500 text-sm">No customer selected</span>
                  )}
                </div>
                <span className="text-gray-300">|</span>
                
                <div className="flex flex-wrap gap-2 text-sm">
                  <button
                    onClick={handleCustomerPayment}
                    disabled={!selectedCustomer}
                    className={`flex items-center gap-1.5 ${selectedCustomer
                      ? 'text-primary hover:text-primary-hover cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                      }`}
                    title={selectedCustomer ? "Track Payment" : "Select a customer to track payment"}
                  >
                    <span className="text-lg">$</span> Payment History
                  </button>

                  <button
                    onClick={handleViewProject}
                    disabled={!selectedCustomer}
                    className={`flex items-center gap-1.5 ${selectedCustomer
                      ? 'text-primary hover:text-primary-hover cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                      }`}
                    title={selectedCustomer ? "View Project" : "Select a customer to view projects"}
                  >
                    <Eye className="size-4" /> View Project
                  </button>

                  <button
                    onClick={handleCreateProject}
                    disabled={!selectedCustomer}
                    className={`flex items-center gap-1.5 ${selectedCustomer
                      ? 'text-primary hover:text-primary-hover cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                      }`}
                    title={selectedCustomer ? "Create Project" : "Select a customer to create project"}
                  >
                    <FilePlus className="size-4" /> Create Project
                  </button>

                  <button
                    onClick={handleTrackEstimateAndInvoice}
                    disabled={!selectedCustomer}
                    className={`flex items-center gap-1.5 ${selectedCustomer
                      ? 'text-primary hover:text-primary-hover cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                      }`}
                    title={selectedCustomer ? "Document Sent" : "Select a customer to view document sent"}
                  >
                    <Eye className="size-4" /> Document Sent
                  </button>

                  <button
                    onClick={handleEdit}
                    disabled={!selectedCustomer}
                    className={`flex items-center gap-1.5 ${selectedCustomer
                      ? 'text-primary hover:text-primary-hover cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                      }`}
                    title={selectedCustomer ? "Edit Customer" : "Select a customer to edit"}
                  >
                    <Edit className="size-4" /> Edit
                  </button>

                  <button
                    onClick={handleDeleteCustomer}
                    disabled={!selectedCustomer}
                    className={`flex items-center gap-1.5 ${selectedCustomer
                      ? 'text-primary hover:text-primary-hover cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                      }`}
                    title={selectedCustomer ? "Delete Customer" : "Select a customer to delete"}
                  >
                    <Trash className="size-4" /> Delete Customer
                  </button>
                </div>
              </div>
              
              {customers.length > 0 ? (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="w-12 px-4 py-3"></th>
                          <th className="px-4 py-3 text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">Name</span>
                              <div className="flex flex-col">
                                <button onClick={() => handleSortByLastName('asc')} className="text-gray-500 hover:text-gray-700">
                                  <ChevronUp className="size-3" />
                                </button>
                                <button onClick={() => handleSortByLastName('desc')} className="text-gray-500 hover:text-gray-700">
                                  <ChevronDown className="size-3" />
                                </button>
                              </div>
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left font-semibold">Address</th>
                          <th className="px-4 py-3 text-left font-semibold">Email</th>
                          <th className="px-4 py-3 text-left font-semibold">Phone</th>
                          <th className="px-4 py-3 text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">Created On</span>
                              <div className="flex flex-col">
                                <button onClick={() => handleSortByDate('asc')} className="text-gray-500 hover:text-gray-700">
                                  <ChevronUp className="size-3" />
                                </button>
                                <button onClick={() => handleSortByDate('desc')} className="text-gray-500 hover:text-gray-700">
                                  <ChevronDown className="size-3" />
                                </button>
                              </div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {customers.map((customer) => (
                          <tr key={customer.customer_id}>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="radio"
                                name="customerSelectDesktop"
                                onChange={() => setSelectedCustomerId(customer.customer_id)}
                                checked={selectedCustomerId === customer.customer_id}
                                className="size-4 cursor-pointer"
                                style={{ accentColor: 'rgb(248, 98, 3)' }}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleEditCustomer(customer)}
                                className="text-foreground hover:text-primary underline font-medium"
                              >
                                {customer.first_name} {customer.last_name}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-paragraph">
                              {formatAddress(customer.address_1, '', customer.city, customer.state, customer.zip)}
                            </td>
                            <td className="px-4 py-3">{customer.email}</td>
                            <td className="px-4 py-3">{customer.phone}</td>
                            <td className="px-4 py-3 text-paragraph">
                              {customer.created_at && formatTimestamp(customer.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-3">
                    {customers.map((customer) => (
                      <div
                        key={customer.customer_id}
                        className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <input
                            type="radio"
                            name="customerSelectMobile"
                            onChange={() => setSelectedCustomerId(customer.customer_id)}
                            checked={selectedCustomerId === customer.customer_id}
                            className="size-4 cursor-pointer mt-1"
                            style={{ accentColor: 'rgb(248, 98, 3)' }}
                          />
                          <div className="flex-1">
                            <button
                              onClick={() => handleEditCustomer(customer)}
                              className="text-foreground hover:text-primary underline font-semibold text-lg"
                            >
                              {customer.first_name} {customer.last_name}
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm ml-7">
                          <div>
                            <span className="text-gray-600">Address: </span>
                            <span className="text-paragraph">
                              {formatAddress(customer.address_1, '', customer.city, customer.state, customer.zip)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Email: </span>
                            <span className="text-paragraph">{customer.email}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Phone: </span>
                            <span className="text-paragraph">{customer.phone}</span>
                          </div>
                          {customer.created_at && (
                            <div>
                              <span className="text-gray-600">Created: </span>
                              <span className="text-paragraph">{formatTimestamp(customer.created_at)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-paragraph mb-6">No customer found, please click the Create Customer button.</p>
                  <Button onClick={handleCreateCustomer} size="medium" variant="default">
                    <FilePlus className="size-5" />
                    Create Customer
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CustomerPage;